import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchModules, sendAnalysis } from "@/lib/api";
import { analyzeStream, chatStream } from "@/lib/aiStream";
import Markdown from "@/components/Markdown";
import RecipientsDialog from "@/components/RecipientsDialog";
import {
  Sparkles,
  X,
  Send,
  Loader2,
  Wand2,
  Bot,
  User,
  StopCircle,
  MessagesSquare,
  Mail,
  Users,
} from "lucide-react";

const SUGGESTED = [
  "Cabang mana yang paling turun bulan Mei?",
  "Berikan 3 rekomendasi untuk menaikkan penjualan.",
  "Kategori diskon mana yang paling banyak dipakai?",
  "Cabang mana yang pertumbuhan pelanggannya paling stagnan?",
];

export default function AiDrawer({ open, onClose, initialScope = "overview" }) {
  const { data: modules = [] } = useQuery({ queryKey: ["modules"], queryFn: fetchModules });
  const [scope, setScope] = useState(initialScope);
  const [messages, setMessages] = useState([]); // {role, content, streaming?}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => "sess-" + Math.random().toString(36).slice(2));
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  const currentModule = modules.find((m) => m.id === scope);

  // The most recent AI assistant message content (for sending)
  const lastAiContent = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant" && messages[i].content) {
        return messages[i].content;
      }
    }
    return "";
  })();

  const sendMut = useMutation({
    mutationFn: () =>
      sendAnalysis(
        scope,
        lastAiContent,
        currentModule ? `Analisis AI — ${currentModule.name}` : "Analisis AI"
      ),
    onSuccess: (res) => {
      const okCount = res.sent?.length || 0;
      const failCount = res.failed?.length || 0;
      if (failCount === 0) {
        toast.success(`Email terkirim ke ${okCount} penerima`);
      } else {
        toast.warning(`${okCount} berhasil, ${failCount} gagal`);
      }
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Gagal kirim email");
    },
  });

  useEffect(() => {
    setScope(initialScope);
  }, [initialScope, open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
    setBusy(false);
  };

  const runAnalyze = async () => {
    if (busy) return;
    setBusy(true);
    const idx = messages.length;
    setMessages((m) => [
      ...m,
      {
        role: "user",
        content:
          scope === "overview"
            ? "Berikan analisis komprehensif seluruh data dashboard."
            : `Analisis modul ${
                modules.find((x) => x.id === scope)?.name || scope
              }.`,
      },
      { role: "assistant", content: "", streaming: true },
    ]);
    const controller = new AbortController();
    abortRef.current = controller;
    await analyzeStream(scope, {
      signal: controller.signal,
      onDelta: (t) => {
        setMessages((m) => {
          const copy = [...m];
          copy[idx + 1] = {
            ...copy[idx + 1],
            content: (copy[idx + 1].content || "") + t,
          };
          return copy;
        });
      },
      onError: (e) => {
        setMessages((m) => {
          const copy = [...m];
          copy[idx + 1] = {
            role: "assistant",
            content: `**Terjadi kesalahan**: ${e.message}`,
          };
          return copy;
        });
      },
      onDone: () => {
        setMessages((m) => {
          const copy = [...m];
          if (copy[idx + 1]) copy[idx + 1] = { ...copy[idx + 1], streaming: false };
          return copy;
        });
      },
    });
    setBusy(false);
  };

  const runChat = async (question) => {
    if (busy || !question.trim()) return;
    setBusy(true);
    const history = messages.filter((m) => !m.streaming).slice(-8);
    const idx = messages.length;
    setMessages((m) => [
      ...m,
      { role: "user", content: question },
      { role: "assistant", content: "", streaming: true },
    ]);
    setInput("");
    const controller = new AbortController();
    abortRef.current = controller;
    await chatStream(
      { question, scope, session_id: sessionId, history: history.map((h) => ({ role: h.role, content: h.content })) },
      {
        signal: controller.signal,
        onDelta: (t) => {
          setMessages((m) => {
            const copy = [...m];
            copy[idx + 1] = {
              ...copy[idx + 1],
              content: (copy[idx + 1].content || "") + t,
            };
            return copy;
          });
        },
        onError: (e) => {
          setMessages((m) => {
            const copy = [...m];
            copy[idx + 1] = {
              role: "assistant",
              content: `**Terjadi kesalahan**: ${e.message}`,
            };
            return copy;
          });
        },
        onDone: () => {
          setMessages((m) => {
            const copy = [...m];
            if (copy[idx + 1]) copy[idx + 1] = { ...copy[idx + 1], streaming: false };
            return copy;
          });
        },
      }
    );
    setBusy(false);
  };

  const clear = () => {
    setMessages([]);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ai-drawer">
      {/* backdrop */}
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />

      <aside className="relative w-full max-w-xl bg-white border-l border-slate-200 flex flex-col h-full shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-start justify-between shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0047FF] to-[#1F2937] flex items-center justify-center rounded-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-slate-400">
                Powered by Claude Sonnet 4.6
              </div>
              <h3 className="font-display font-black text-lg tracking-tight">
                AI Analisis
              </h3>
              <p className="text-xs text-slate-500">
                Tanya apa saja tentang data dashboard Anda.
              </p>
            </div>
          </div>
          <button
            data-testid="ai-close"
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scope + actions */}
        <div className="border-b border-slate-200 px-6 py-3 flex items-center gap-2 flex-wrap shrink-0 bg-slate-50">
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400">
            Lingkup:
          </span>
          <select
            data-testid="ai-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="text-xs border border-slate-300 bg-white px-2 py-1.5 focus:outline-none focus:border-[#0047FF]"
          >
            <option value="overview">Semua Data (Ikhtisar)</option>
            {modules.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <button
            data-testid="ai-analyze"
            onClick={runAnalyze}
            disabled={busy}
            className="btn-sharp ml-auto inline-flex items-center gap-1.5 bg-[#0047FF] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#0033CC] disabled:opacity-50"
          >
            <Wand2 className="w-3.5 h-3.5" />
            Analisis Cepat
          </button>
          <button
            data-testid="ai-manage-recipients"
            onClick={() => setRecipientsOpen(true)}
            className="btn-sharp inline-flex items-center gap-1.5 border border-slate-300 bg-white text-slate-700 text-xs font-semibold px-3 py-1.5 hover:border-slate-900"
            title="Kelola penerima email"
          >
            <Users className="w-3.5 h-3.5" />
            Penerima
          </button>
          {lastAiContent && (
            <button
              data-testid="ai-send-email"
              onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || busy}
              className="btn-sharp inline-flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-50"
              title="Kirim analisis ke Email + WhatsApp penerima"
            >
              {sendMut.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Mail className="w-3.5 h-3.5" />
              )}
              Kirim Notifikasi
            </button>
          )}
          {messages.length > 0 && (
            <button
              data-testid="ai-clear"
              onClick={clear}
              disabled={busy}
              className="btn-sharp text-xs font-semibold text-slate-600 border border-slate-300 px-3 py-1.5 hover:border-slate-500"
            >
              Bersihkan
            </button>
          )}
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
          data-testid="ai-messages"
        >
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="w-14 h-14 mx-auto bg-slate-100 flex items-center justify-center mb-4">
                <MessagesSquare className="w-6 h-6 text-slate-400" />
              </div>
              <div className="font-display font-bold text-slate-900 mb-1">
                Mulai analisis data Anda
              </div>
              <p className="text-xs text-slate-500 max-w-xs mx-auto mb-4">
                Klik <b>Analisis Cepat</b> untuk ringkasan otomatis, atau ajukan
                pertanyaan spesifik di bawah.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-md mx-auto">
                {SUGGESTED.map((q) => (
                  <button
                    key={q}
                    data-testid={`ai-suggest-${q.slice(0, 15)}`}
                    onClick={() => runChat(q)}
                    className="text-xs border border-slate-300 px-3 py-1.5 hover:border-[#0047FF] hover:text-[#0047FF] text-left"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className="flex gap-3">
              <div
                className={`w-8 h-8 shrink-0 flex items-center justify-center ${
                  m.role === "user" ? "bg-slate-900 text-white" : "bg-[#0047FF] text-white"
                }`}
              >
                {m.role === "user" ? (
                  <User className="w-4 h-4" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">
                  {m.role === "user" ? "Anda" : "AI Analyst"}
                  {m.streaming && (
                    <span className="ml-2 text-[#0047FF] normal-case tracking-normal">
                      · mengetik…
                    </span>
                  )}
                </div>
                {m.role === "user" ? (
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">
                    {m.content}
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 px-4 py-3">
                    {m.content ? (
                      <Markdown text={m.content} />
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Menganalisis data...
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 px-6 py-4 shrink-0 bg-white">
          <div className="flex gap-2">
            <input
              data-testid="ai-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && runChat(input)}
              placeholder="Tanyakan sesuatu tentang data..."
              disabled={busy}
              className="flex-1 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-[#0047FF] disabled:bg-slate-50"
            />
            {busy ? (
              <button
                data-testid="ai-stop"
                onClick={stop}
                className="btn-sharp inline-flex items-center gap-1.5 bg-rose-600 text-white text-sm font-semibold px-4 py-2 hover:bg-rose-700"
              >
                <StopCircle className="w-4 h-4" /> Berhenti
              </button>
            ) : (
              <button
                data-testid="ai-send"
                onClick={() => runChat(input)}
                disabled={!input.trim()}
                className="btn-sharp inline-flex items-center gap-1.5 bg-[#0047FF] text-white text-sm font-semibold px-4 py-2 hover:bg-[#0033CC] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" /> Kirim
              </button>
            )}
          </div>
          <div className="text-[10px] text-slate-400 mt-2 mono">
            AI dapat membuat kesalahan. Selalu verifikasi angka penting.
          </div>
        </div>
      </aside>

      {/* Recipients management dialog */}
      {recipientsOpen && (
        <RecipientsDialog
          module={currentModule || { id: "overview", name: "Ikhtisar" }}
          onClose={() => setRecipientsOpen(false)}
        />
      )}
    </div>
  );
}
